import Dexie, { type Table } from "dexie";

import { ONE, TWENTY_FIVE, ZERO } from "@/app/constants/constants";
import { PAGINATION_SCOPE_SEGMENT_DEPTH } from "@/app/constants/routes_sidebar/routes.constant";
import type { PaginationMeta } from "@/components/pagination/hooks/usePagination";

interface PaginationRecord {
  key: string;
  value: PaginationMeta;
  updatedAt: number;
}

class PaginationDB extends Dexie {
  pagination!: Table<PaginationRecord, string>;

  constructor() {
    super("WpxPaginationDB");
    this.version(1).stores({
      pagination: "key, updatedAt",
    });
  }
}

const paginationDb = new PaginationDB();

export const DEFAULT_PAGINATION: PaginationMeta = {
  limit: TWENTY_FIVE,
  offset: ZERO,
  currentPage: ONE,
};

const normalizeSegment = (segment: string | undefined): string | undefined => {
  if (!segment || segment.startsWith(":")) {
    return undefined;
  }

  return segment;
};
/**
 * Creates a stable IndexedDB key for a table pagination state.
 *
 * @param {string} pathname - Current route pathname.
 * @param {string} activeTab - Optional active tab identifier.
 * @param {string} tableId - Optional table identifier for multi-table pages.
 * @returns {string} Stable storage key for the table pagination state.
 */
export const generatePaginationKey = (
  pathname: string,
  activeTab?: string,
  tableId?: string,
): string => {
  const routeKey = pathname || "/";
  const keyParts = [routeKey, activeTab, tableId].filter(Boolean);

  return keyParts.join("::");
};

/**
 * Reads persisted pagination for a table key.
 *
 * @param {string} key - Storage key generated for the table.
 * @returns {Promise<PaginationMeta | null>} Persisted pagination or null if no record exists.
 */
export const getPagination = async (
  key: string,
): Promise<PaginationMeta | null> => {
  const record = await paginationDb.pagination.get(key);

  return record?.value ?? null;
};

/**
 * Persists pagination for a table key.
 *
 * @param {string} key - Storage key generated for the table.
 * @param {PaginationMeta} value - Pagination state to persist.
 * @returns {Promise<void>} Promise resolved after the pagination state is saved.
 */
export const setPagination = async (
  key: string,
  value: PaginationMeta,
): Promise<void> => {
  await paginationDb.pagination.put({
    key,
    value,
    updatedAt: Date.now(),
  });
};

/**
 * Removes persisted pagination for a table key.
 *
 * @param {string} key - Storage key generated for the table.
 * @returns {Promise<void>} Promise resolved after the pagination state is removed.
 */
/**
 * Removes every persisted pagination entry that belongs to a module.
 *
 * Storage keys are generated as `${pathname}::${activeTab}::${tableId}`. The
 * module path is the leading route segment (see getPaginationModulePath), so
 * any key whose pathname starts with that module prefix is owned by the
 * module and must be cleared when the user navigates away from it.
 *
 * @param {string} modulePath - Module path returned by getPaginationModulePath.
 * @returns {Promise<void>} Promise resolved after all matching entries are removed.
 */
export const clearPaginationByModule = async (
  modulePath: string,
): Promise<void> => {
  if (!modulePath || modulePath === "/") {
    return;
  }

  const prefix = modulePath.endsWith("/") ? modulePath : `${modulePath}/`;

  await paginationDb.pagination
    .filter((record) => {
      const pathnamePart = record.key.split("::")[0] ?? "";

      return pathnamePart === modulePath || pathnamePart.startsWith(prefix);
    })
    .delete();
};

/**
 * Removes all persisted pagination entries from IndexedDB.
 *
 * @returns {Promise<void>} Promise resolved after all pagination state is removed.
 */
export const clearAllPagination = async (): Promise<void> => {
  await paginationDb.pagination.clear();
};

/**
 * Resolves the high-level module path used to detect cross-module navigation.
 *
 * Scope depth defaults to one segment so `/reports/foo` and `/reports/bar`
 * share the same module. Roots registered in PAGINATION_SCOPE_SEGMENT_DEPTH
 * with depth 2 widen the module identity to include the second segment, so
 * `/clients/customer` and `/clients/prospect` reset against each other.
 *
 * @param {string} pathname - Current route pathname.
 * @returns {string} Module path used for pagination reset boundaries.
 */
export const getPaginationModulePath = (pathname: string): string => {
  const [firstSegment, secondSegment] = pathname
    .split("/")
    .map((segment) => normalizeSegment(segment))
    .filter(Boolean);

  if (!firstSegment) {
    return "/";
  }

  const rootPath = `/${firstSegment}`;
  const scopeDepth = PAGINATION_SCOPE_SEGMENT_DEPTH[rootPath] ?? 1;

  if (scopeDepth === 2 && secondSegment) {
    return `${rootPath}/${secondSegment}`;
  }

  return rootPath;
};
